import React from "react";
import { Lock } from "lucide-react";
import { useActivePaymentBranding } from "../lib/settings";

/**
 * Trust signal — provider-aware payment banner.
 *
 * - If the active payment provider is Netopia → renders the official NETOPIA
 *   Payments banner (NETOPIA + Mastercard + VISA) as before.
 * - Otherwise (EuPlătesc / neutral) → renders a clean neutral card with a
 *   Lock icon and the provider's `securePaymentText` so we never display a
 *   third-party logo we are not licensed to use on EuPlătesc transactions.
 *
 * The component name is kept as `NetopiaPaymentsBanner` for grep-friendly
 * git history; callers don't need to change anything.
 *
 * Placement contract on the checkout page:
 *   - Desktop (>= lg): sits in the right sidebar, BELOW the order summary.
 *   - Mobile/tablet (< lg): renders INSIDE the form, just above the submit
 *     button, so users see the trust signal at the moment of conversion.
 */
// `prima`: false for a cohort that does not take the Prima de Carieră Didactică
// card (12.10.2026); the sentence about the card is then left out.
export default function NetopiaPaymentsBanner({ className = "", prima = true }) {
  const branding = useActivePaymentBranding();

  if (branding.showNetopiaBanner) {
    return (
      <a
        href="https://netopia-payments.com"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Plată securizată prin NETOPIA Payments"
        data-testid="netopia-payments-banner"
        className={
          "block rounded-2xl border border-[#E5E7EB] bg-white p-4 shadow-sm " +
          "transition-all hover:shadow-md hover:border-[#D1D5DB] " +
          "focus:outline-none focus:ring-2 focus:ring-[#965EA7]/30 " +
          className
        }
      >
        <img
          src="/assets/netopia-payments-banner.svg"
          alt="NETOPIA Payments — procesator de plăți (Mastercard, Visa)"
          loading="lazy"
          className="w-full h-auto object-contain"
          width={418}
          height={75}
        />
      </a>
    );
  }

  // Non-Netopia (e.g. EuPlătesc): neutral trust card. No external link
  // because we don't ship third-party branded assets here. The visual is
  // a calm, single-accent card with a prominent Lock + concise copy.
  return (
    <div
      data-testid="payment-trust-neutral"
      className={
        "rounded-2xl border border-[#E5E7EB] bg-gradient-to-br from-white to-[#F8F5FB] " +
        "p-5 shadow-sm " +
        className
      }
    >
      <div className="flex items-start gap-3">
        <div
          className="flex-none inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#965EA7]/10 ring-1 ring-[#965EA7]/15"
          aria-hidden="true"
        >
          <Lock size={18} className="text-[#965EA7]" strokeWidth={2.25} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-[#373A45] leading-tight">
            {branding.securePaymentText}
          </div>
          <div className="mt-1.5 text-xs text-[#6B7280] leading-relaxed">
            Datele cardului sunt introduse pe pagina securizată a procesatorului.
            {prima && " Acceptăm și cardul Prima de carieră didactică."}
          </div>
          <div className="mt-2.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-[#519872]">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#519872]" />
            3D Secure
          </div>
        </div>
      </div>
    </div>
  );
}
